import { Formik } from 'formik';

import { getFragmentData } from '@e-socna/data-layer';
import { invariant } from '@e-socna/utils';
import type { FragmentType, Id } from '@e-socna/data-layer';

import { FormVariant } from '../enums/variant.enum';
import { useMemos } from '../MemoBeforeSubmit/hooks/useMemos.hook';

import { useInitialValuesFactory } from './hooks/useInitialValuesFactory.hook';
import { useCostSubmit } from './hooks/useSubmit.hook';
import { useCostSchema } from './hooks/useCostSchema.hook';
import { CostForm_FixedCost_Fragment } from './fragments/fixed-cost.fragment';
import { CostForm_VariableCost_Fragment } from './fragments/variable-cost.fragment';
import { CostFormFields } from './CostFormFields';
import { isCostValueConfidential } from './fragments/cost.fragment';
import type { CostMutationResult } from './hooks/useSubmit.hook';
import { InfoAlert } from '../../components/InfoAlert';

interface Props {
  readonly askMemoBeforeSubmit?: boolean;
  readonly costId?: Id;
  readonly formVariant?: FormVariant;
  readonly initialFixedCostValues?: FragmentType<
    typeof CostForm_FixedCost_Fragment
  >;
  readonly initialVariableCostValues?: FragmentType<
    typeof CostForm_VariableCost_Fragment
  >;
  readonly onCancel?: () => void;
  readonly onSuccess?: (result: CostMutationResult) => void;
  readonly setMustBlockAction?: (mustBlock: boolean) => void;
  readonly showCancelButton?: boolean;
  readonly title?: string;
}

export const CostForm = ({
  askMemoBeforeSubmit = true,
  costId,
  formVariant,
  initialFixedCostValues,
  initialVariableCostValues,
  onCancel: handleCancel,
  onSuccess: handleSuccess,
  setMustBlockAction,
  showCancelButton,
  title,
}: Readonly<Props>) => {
  invariant(
    !!(formVariant !== FormVariant.Modification || costId),
    'You must provide a costId when formVariant is Modification',
  );

  invariant(
    !initialFixedCostValues || !initialVariableCostValues,
    'You must provide either initialFixedCostValues or initialVariableCostValues, not both',
  );

  const pInitialValues = initialFixedCostValues
    ? getFragmentData(CostForm_FixedCost_Fragment, initialFixedCostValues)
    : initialVariableCostValues
      ? getFragmentData(
          CostForm_VariableCost_Fragment,
          initialVariableCostValues,
        )
      : undefined;

  const { clearMemos, memos, setMemos } = useMemos();

  const CostSchema = useCostSchema();

  const { formError, handleSubmit } = useCostSubmit({
    costId,
    formVariant,
    handleSuccess,
    memos,
  });

  const submitLabel =
    !formVariant || formVariant === FormVariant.Creation ? 'Créer' : 'Modifier';

  const { initialValues } = useInitialValuesFactory(pInitialValues);

  if (pInitialValues && isCostValueConfidential(pInitialValues)) {
    return (
      <InfoAlert>
        La valeur de ce coût est confidentielle : vous ne pouvez pas le
        modifier.
      </InfoAlert>
    );
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={CostSchema}
    >
      <CostFormFields
        askMemoBeforeSubmit={askMemoBeforeSubmit}
        clearMemos={clearMemos}
        formError={formError}
        formVariant={formVariant}
        initialValues={initialValues}
        memos={memos}
        onCancel={handleCancel}
        setMemos={setMemos}
        setMustBlockAction={setMustBlockAction}
        showCancelButton={showCancelButton}
        submitLabel={submitLabel}
        title={title}
      />
    </Formik>
  );
};
